import { isoDate } from 'util/isoDate';
import { convertTo12Hour } from 'util/convertTimeString';
import { Box, Typography } from '@mui/material';
import { CheckCircleOutline, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useEventsApi } from 'services/api';
import { NearbyEventDto } from 'services/api/apis/events/dto';

const INITIAL_VISIBLE_COUNT = 3;
const MAX_VISIBLE_COUNT = 8;

interface EventConflictPanelProps {
  date: Date | null;
  startTime: string;
  startPeriod: string;
  endTime: string;
  endPeriod: string;
  facilityId: string;
  schoolId: string;
  excludeEventId?: string;
}

function formatEventTime (time: string): string {
  return convertTo12Hour(time, { includePeriod: true });
}

function EventCard ({ event }: { event: NearbyEventDto }): ReactElement {
  const location = event.facilityName
    ? `${event.facilityName}${event.facilityRoomArea ? `, ${event.facilityRoomArea}` : ''}`
    : event.venue ?? '';

  const timeRange = `${formatEventTime(event.startTime)} \u2013 ${formatEventTime(event.endTime)}`;

  return (
    <Box
      sx={{
        backgroundColor: event.isConflict ? '#FFFBF5' : '#F8FAFC',
        border: `1px solid ${event.isConflict ? '#F0D9B5' : '#E2E8F0'}`,
        borderRadius: '8px',
        px: 14,
        py: 12,
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '21px',
            color: '#383838',
          }}
        >
          {event.name}
        </Typography>
        <Box>
          {location && (
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '21px',
                color: '#383838',
              }}
            >
              {location}
            </Typography>
          )}
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '21px',
              color: '#383838',
            }}
          >
            {timeRange}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export function EventConflictPanel ({
  date,
  startTime,
  startPeriod,
  endTime,
  endPeriod,
  facilityId,
  schoolId,
  excludeEventId,
}: EventConflictPanelProps): ReactElement {
  const eventsApi = useEventsApi();
  const [expanded, setExpanded] = useState(false);

  const hasDate = !!date;
  const hasTime = !!startTime && !!endTime;
  const hasFacility = !!facilityId;
  const hasAnyInput = hasDate;

  const swrKey = useMemo(() => {
    if (!hasDate || !schoolId) return null;

    const params: Record<string, string> = {
      schoolId,
      date: isoDate(date),
    };

    if (hasTime) {
      params.startTime = `${startTime} ${startPeriod}`;
      params.endTime = `${endTime} ${endPeriod}`;
    }

    if (hasFacility)
      params.facilityId = facilityId;


    if (excludeEventId)
      params.excludeEventId = excludeEventId;


    return JSON.stringify(['nearby-events', params]);
  }, [hasDate, hasTime, hasFacility, date, startTime, startPeriod, endTime, endPeriod, facilityId, schoolId, excludeEventId]);

  const { data, isValidating } = useSWR(
    swrKey,
    async () => {
      if (!date || !schoolId) return null;

      const params: {
        schoolId: string;
        date: string;
        startTime?: string;
        endTime?: string;
        facilityId?: string;
        excludeEventId?: string;
      } = {
        schoolId,
        date: isoDate(date),
      };

      if (hasTime) {
        params.startTime = `${startTime} ${startPeriod}`;
        params.endTime = `${endTime} ${endPeriod}`;
      }

      if (hasFacility)
        params.facilityId = facilityId;


      if (excludeEventId)
        params.excludeEventId = excludeEventId;


      const result = await eventsApi.getNearbyEvents(params);
      if (result.success) return result.value;
      return null;
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 500,
    },
  );

  useEffect(() => {
    setExpanded(false);
  }, [swrKey]);

  const events = data?.events ?? [];
  const count = data?.count ?? 0;
  const cappedEvents = events.slice(0, MAX_VISIBLE_COUNT);
  const visibleEvents = expanded ? cappedEvents : cappedEvents.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = cappedEvents.length - INITIAL_VISIBLE_COUNT;

  return (
    <Box
      sx={{
        backgroundColor: '#F4F4F4',
        border: '1px solid #F0F0F0',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        p: 16,
        width: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '21px',
            color: '#000',
          }}
        >
          Other Events at This Time
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600,
            fontSize: 12,
            lineHeight: '18px',
            color: '#383838',
            textTransform: 'uppercase',
          }}
        >
          {count} {count === 1 ? 'EVENT' : 'EVENTS'}
        </Typography>
      </Box>

      {/* Body */}
      {!hasAnyInput && (
        <Box
          sx={{
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            px: 14,
            py: 12,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '21px',
              color: '#383838',
            }}
          >
            First, select a date, time, and location
          </Typography>
        </Box>
      )}

      {hasAnyInput && count === 0 && !isValidating && (
        <Box
          sx={{
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            px: 14,
            py: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <CheckCircleOutline sx={{ color: '#22C55E', fontSize: 20 }} />
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '21px',
              color: '#383838',
            }}
          >
            No competing events
          </Typography>
        </Box>
      )}

      {hasAnyInput && count > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {visibleEvents.map(event => (
            <EventCard key={event.id} event={event} />
          ))}
        </Box>
      )}

      {hasAnyInput && hiddenCount > 0 && (
        <Box
          onClick={() => setExpanded(!expanded)}
          sx={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DEDEDE',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 24,
            py: 14,
            cursor: 'pointer',
            width: '100%',
            '&:hover': {
              backgroundColor: '#FAFAFA',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '21px',
                color: '#383838',
              }}
            >
              {expanded ? 'Show less' : `Show ${hiddenCount} more event${hiddenCount === 1 ? '' : 's'}`}
            </Typography>
            {expanded ? (
              <KeyboardArrowUp sx={{ color: '#383838', fontSize: 24 }} />
            ) : (
              <KeyboardArrowDown sx={{ color: '#383838', fontSize: 24 }} />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
